import { z } from 'zod';
import { conversationGroupMessageScheme, conversationMessageScheme } from '@/models/conversations/conversation';
import { conversationTypeScheme } from '@/models/conversations/conversation-session';
import {
  deleteChatMessageScheme,
  deleteGroupChatMessageScheme,
  sendChatMessageScheme,
  sendGroupChatMessageScheme,
} from '@/models/messages/send-message';
import { avatarUrlSchema } from '@/utils/schemas';

export const dispatchEventScheme = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('connect'),
    params: z.object({}),
  }),
  z.object({
    action: z.literal('disconnect'),
    params: z.object({}),
  }),
  z.object({
    action: z.literal('reconnect'),
    params: z.object({}),
  }),
  z.object({
    action: z.literal('checkConnection'),
    params: z.object({}),
  }),
  z.object({
    action: z.literal('localeChange'),
    params: z.object({
      locale: z.string(),
    }),
  }),
  z.object({
    action: z.literal('stateChange'),
    params: z.discriminatedUnion('state', [
      z.object({
        state: z.literal('open'),
        endpoint: z.string().nullable(),
      }),
      z.object({
        state: z.enum(['closed', 'ended']),
      }),
    ]),
  }),
  z.object({
    action: z.literal('send'),
    params: z.object({
      message: z.union([
        sendChatMessageScheme,
        sendGroupChatMessageScheme,
        deleteChatMessageScheme,
        deleteGroupChatMessageScheme,
      ]),
      isResend: z.boolean().default(false).optional(),
    }),
  }),
  z.object({
    action: z.literal('message'),
    params: z.discriminatedUnion('type', [
      z.object({
        type: z.literal('chat'),
        message: conversationMessageScheme,
      }),
      z.object({
        type: z.literal('groupChat'),
        message: conversationGroupMessageScheme,
      }),
    ]),
  }),
  z.object({
    action: z.literal('validateMessage'),
    params: z.object({
      type: z.enum(['chat', 'groupChat']),
      sessionId: z.number(),
      validateId: z.number(),
      messageId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('read'),
    params: z.object({
      sessionId: z.number(),
      messageId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('readChatSelf'),
    params: z.object({
      sessionId: z.number(),
      messageId: z.number(),
      timestamp: z.number(),
    }),
  }),
  z.object({
    action: z.literal('newSession'),
    params: z.object({
      userId: z.number(),
      sessionId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('joinGroup'),
    params: z.object({
      groupId: z.number(),
      groupImg: avatarUrlSchema,
      groupName: z.string(),
    }),
  }),
  z.object({
    action: z.literal('disbandGroup'),
    params: z.object({
      groupId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('leaveGroup'),
    params: z.object({
      groupId: z.number(),
      userId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('validateGroup'),
    params: z.object({
      groupId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('updateGroup'),
    params: z.object({
      groupId: z.number(),
      info: z.string().optional(),
      avatarUrl: avatarUrlSchema.optional(),
      name: z.string().optional(),
    }),
  }),
  z.object({
    action: z.literal('readAll'),
    params: z.object({
      sessionId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('groupReadAll'),
    params: z.object({
      groupId: z.number(),
      messageId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('updateToken'),
    params: z.object({
      token: z.string(),
    }),
  }),
  z.object({
    action: z.literal('kickOut'),
    params: z.object({}),
  }),
  z.object({
    action: z.literal('online'),
    params: z.object({
      userId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('offline'),
    params: z.object({
      userId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('deleteMessage'),
    params: z.object({
      type: conversationTypeScheme,
      sessionId: z.number(),
      messageId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('purgeMessages'),
    params: z.object({
      type: conversationTypeScheme.optional(),
      sessionId: z.number().optional(),
      userId: z.number().optional(),
    }),
  }),
  z.object({
    action: z.literal('unsubscribe'),
    params: z.object({
      groupId: z.number(),
    }),
  }),
  z.object({
    action: z.literal('toastInfo'),
    params: z.object({
      type: z.string(),
      msg: z.string(),
    }),
  }),
  z.object({
    action: z.literal('friendApplication'),
    params: z.object({
      applicationId: z.number(),
      userId: z.number(),
      userName: z.string(),
      avatarUrl: avatarUrlSchema,
      remark: z.string().default(''),
    }),
  }),
  z.object({
    action: z.literal('groupApplication'),
    params: z.object({
      groupId: z.number(),
      applicationCount: z.number(),
    }),
  }),
  z.object({
    action: z.literal('muteGroupUser'),
    params: z.object({
      groupId: z.number(),
      userId: z.number(),
      mute: z.boolean(),
    }),
  }),
  z.object({
    action: z.literal('heartbeat'),
    params: z.object({
      timestamp: z.number(),
    }),
  }),
]);
export type DispatchEvent = z.infer<typeof dispatchEventScheme>;
